import { useState } from "react";
import { useFormik } from "formik";
import { Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { object, string, number } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";

import MuiSelect from "../../components/common/select/MuiSelect";

import { PartnerSchema } from "../../services/Schemas/Partner";
import { PartnersListRequest } from "../../services/Endpoints/partners/PartnerEndpoint";
import { useCanAccess } from "../../hooks/canAccess";
import { AccessEnums } from "../../constants/accessEnum";

interface ICreateRoleFrom {
  roleInfo?: Partial<PartnerSchema>;
  onSuccess: () => void;
}

export default function CreatePartnerForm({
  roleInfo,
  onSuccess,
}: ICreateRoleFrom) {
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const loginSchema = object({
    caption: string().min(4, "نام ضروری است").required("نام ضروری است"),
    support_fullname: string().required("نام پشتیبانی ضروری است"),
    status: string().min(4, "بخش ضروری است").required(),
    support_phone: string().optional(),
    max_pending_calls: number().optional(),
  });

  const formik = useFormik({
    initialValues: {
      caption: roleInfo?.caption ?? "",
      support_fullname: roleInfo?.support_fullname ?? "",
      status: roleInfo?.status ?? "active",
      support_phone: roleInfo?.support_phone ?? "",
      max_pending_calls: roleInfo?.max_pending_calls ?? 0,
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      console.log(values);
      try {
        const {
          caption,
          status,
          support_fullname,
          support_phone,
          max_pending_calls,
        } = values;

        const contactUpdateRequest = new PartnersListRequest(protectedInstance);

        const resp =
          roleInfo && roleInfo.id
            ? await contactUpdateRequest.update(roleInfo.id.toString(), {
                caption,
                status: status as "active",
                support_fullname,
                support_phone,
                max_pending_calls,
              })
            : await contactUpdateRequest.create({
                caption,
                status: status as "active",
                support_fullname,
                support_phone,
                max_pending_calls,
              });

        setWaiting(false);

        formik.resetForm();
        setNotification({
          type: "success",
          message: `شریک با موفقیت ${roleInfo ? "آپدیت" : "ایجاد"} شد.`,
        });
        onSuccess();
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });

  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="20.31px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <MuiTextField
        width="100%"
        label={"نام"}
        placeholder={"نام شریک جدید را وارد کنید"}
        name={"caption"}
        type="text"
        value={formik.values.caption}
        handleChange={formik.handleChange}
        error={
          formik.touched.caption &&
          Boolean(formik.errors.caption) &&
          formik.errors.caption
        }
      />
      <MuiTextField
        width="100%"
        label={"نام پشتیبانی"}
        placeholder={"نام پشتیبانی  را وارد کنید"}
        name={"support_fullname"}
        type="text"
        value={formik.values.support_fullname}
        handleChange={formik.handleChange}
        error={
          formik.touched.support_fullname &&
          Boolean(formik.errors.support_fullname) &&
          formik.errors.support_fullname
        }
      />
      <MuiSelect
        placeholder={"وضعیت  را انتخاب کنید"}
        name={"status"}
        label={"وضعیت"}
        value={formik.values.status}
        handleChange={formik.handleChange}
        options={[
          {
            value: "active",
            label: "فعال",
          },
          {
            value: "inactive",
            label: "غیر فعال",
          },
        ]}
      />
      <MuiTextField
        width="100%"
        label={"شماره تماس"}
        placeholder={"شماره پشتیبانی  را وارد کنید"}
        name={"support_phone"}
        type="text"
        value={formik.values.support_phone}
        handleChange={formik.handleChange}
        error={
          formik.touched.support_phone &&
          Boolean(formik.errors.support_phone) &&
          formik.errors.support_phone
        }
      />
      <MuiTextField
        width="100%"
        label={"حداکثر تماس درحال انتظار"}
        placeholder={"حداکثر تماس درحال انتظار را وارد کنید"}
        name={"max_pending_calls"}
        type="number"
        value={formik.values.max_pending_calls}
        inputMode="numeric"
        handleChange={formik.handleChange}
        error={
          formik.touched.support_phone &&
          Boolean(formik.errors.support_phone) &&
          formik.errors.support_phone
        }
      />
      <LoadingButton
        variant="confirm"
        sx={{
          mt: "10px",
        }}
        type="submit"
        loading={waiting}
        disabled={!can(AccessEnums.AdminPartnerCreate)}
      >
        تایید
      </LoadingButton>
    </Stack>
  );
}
